import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Sparkles, X, Flame, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Move 2 — weekly identity review surface.
 *
 * Renders nothing until /api/ai/weekly-review reports a review is due.
 * When the user clicks "Reflect now," the prompt is passed up via onEngage so
 * the AI chat can seed its conversation with it. We also POST acknowledge so the
 * 7-day clock resets even if the user doesn't reply.
 *
 * "Not today" hides the card for this session only (no acknowledge). The review
 * re-appears on next reload until 7 days pass OR the user engages.
 *
 * "Skip this week" acknowledges without engaging — resets the 7-day clock.
 */
export default function WeeklyReviewCard({ token, onEngage, onDueChange }) {
  const { t, i18n } = useTranslation();
  const [review, setReview] = useState(null);
  const [hiddenForSession, setHiddenForSession] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  const onDueChangeRef = useRef(onDueChange);
  useEffect(() => {
    onDueChangeRef.current = onDueChange;
  }, [onDueChange]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/ai/weekly-review`, {
          params: { lng: i18n.language },
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!cancelled) {
          if (data.due) {
            setReview(data);
            onDueChangeRef.current?.(true);
          } else {
            onDueChangeRef.current?.(false);
          }
        }
      } catch (_) {
        if (!cancelled) {
          onDueChangeRef.current?.(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token, i18n.language]);

  const acknowledge = async () => {
    try {
      await axios.post(`${API}/ai/weekly-review/acknowledge`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (_) {
      // Best-effort. If this fails the user will see the prompt again sooner.
    }
  };

  const handleEngage = async () => {
    setAcknowledging(true);
    await acknowledge();
    onEngage?.(review.prompt);
    setReview(null);
    onDueChangeRef.current?.(false);
    setAcknowledging(false);
  };

  const handleSkipWeek = async () => {
    setAcknowledging(true);
    await acknowledge();
    setReview(null);
    onDueChangeRef.current?.(false);
    setAcknowledging(false);
  };

  if (!review || hiddenForSession) return null;

  const { focus_being: focus, stats } = review;
  const isHabit = focus?.type === 'habit';

  return (
    <section
      className="rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 fade-in relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, hsl(36 40% 96%) 0%, hsl(120 14% 96%) 100%)',
        border: '1px solid hsl(36 20% 85% / 0.7)'
      }}
      data-testid="weekly-review-card"
    >
      <button
        type="button"
        onClick={() => {
          setHiddenForSession(true);
          onDueChangeRef.current?.(false);
        }}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
        title={t('review.dismiss')}
        data-testid="review-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-accent" />
        <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t('review.label')}</span>
        {focus && (
          <>
            <span className="text-xs text-muted-foreground">·</span>
            <span className="text-xs text-muted-foreground italic">{focus.title}</span>
            {isHabit && stats?.streak > 0 && (
              <span className="text-xs text-orange-600 dark:text-orange-400 flex items-center gap-0.5 ml-1">
                <Flame className="w-3 h-3" /> {stats.streak}
              </span>
            )}
          </>
        )}
      </div>

      <p className="font-serif text-base sm:text-lg leading-relaxed mb-4 max-w-2xl">
        {review.prompt}
      </p>

      {stats && (
        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground mb-4">
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3 h-3" />
            {t('review.statsCompleted', { done: stats.completed_last_7d, total: stats.total_relevant })}
          </span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          onClick={handleEngage}
          disabled={acknowledging}
          className="rounded-full"
          data-testid="review-engage-btn"
        >
          {t('review.engage')}
          <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </Button>
        <Button
          variant="ghost"
          onClick={handleSkipWeek}
          disabled={acknowledging}
          className="rounded-full text-muted-foreground"
          data-testid="review-skip-btn"
        >
          {t('review.skipWeek')}
        </Button>
      </div>
    </section>
  );
}
